import React, { useCallback, useMemo, useState } from 'react'
import {
  useCurrentCloudProvider,
  useSyncCloudMarketplace,
  SnackBar,
  useAccountId,
  standardLabraDateFormat,
} from '@labrav/common'
import { useStyles } from './ContractListingV2.styles'
import { useDispatch, useSelector } from '../../../store'
import {
  type Contract,
  type ContractListingDimensionFields,
  type ContractsListingState,
  type Entitlement,
} from '../../../modules/contractsListing/types'
import {
  setContracts,
  updateEntitlementsOrDimensions,
  sendContractsDataV2,
} from '../../../modules/contractsListing/action'
import {
  ButtonV2,
  CardsContainer,
  LabraToolTipV2,
  OfferCard,
  PlanType,
  TableComponentV2,
  TextInputV2,
  Clipboard as CustomClipboard,
} from '@labrav/react-components'
import clsx from 'clsx'
import { DateTime as DateTimeLuxon } from 'luxon'
import { isEmpty } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'
import { type CloudMarketplace } from '../../../modules/productsListing/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TableCell } from '@mui/material'
import {
  faDownload,
  faExternalLinkAlt,
  faUpload,
} from '@fortawesome/pro-regular-svg-icons'
import {
  contractSendMessage,
  numberKeyToPrevent,
} from '../../../common/utils/messagesConstants'
import { ContractsV2FooterSubmit } from './ContractsV2FooterSubmit'
import { ContractListingV2Modal } from './ContractListingV2Modal'
import CreateMeteringFormatSelector from '../../FlyOutUsageMetering/CreateMetering/modals/CreateMeteringFormatSelector/CreateMeteringFormatSelector'
import { DownloadMeteringHistoryModal } from '../../FlyOutUsageMetering/DownloadMeteringHistory/DownloadMeteringHistoryModal/DownloadMeteringHistoryModal'
import { transformMetric } from '../../FlyOutUsageMetering/UsageListingView/UsageListingView'
import { formatToDecimalPlaces } from '../../../common/utils/meteringHelpers'
import { ResponsiveColumn } from '../../../common/components/ResponsiveColumn/ResponsiveColumn'
import { BuyerLogo } from '../../../common/components/BuyerLogo/BuyerLogo'

export { BuyerLogo }

export const mapping = {
  Dimensions: 'dimensionLabel',
  'Price per bandwidth': 'unit',
  Quantity: 'qty',
  Total: 'total',
}
interface MeteringData {
  [contractId: string]: {
    [entitlementId: string]: {
      quantity: string
      total: number | null
    }
  }
}

export interface ChangedContractInfo {
  buyerLogoUrl: string
  buyerIdentifier: string
  totalAmount: number | null
}

const getKeyNameForUpdateMetering = (cloud: CloudMarketplace) => {
  switch (cloud) {
    case 'AWS': {
      return 'entitlements'
    }
    case 'AZURE': {
      return 'dimensions'
    }
    case 'GCP': {
      return 'plan'
    }
  }
}

const ActionButtons = ({
  updateDownloadModalVisibility,
  openDownloadModal,
  updateUploadModalVisibility,
  openUploadModal,
}: {
  updateDownloadModalVisibility: () => void
  openDownloadModal: boolean
  updateUploadModalVisibility: () => void
  openUploadModal: boolean
}) => {
  const { classes } = useStyles()
  return (
    <div className={classes.btnContainer}>
      <ButtonV2
        smallVariant
        data-testid="download-history-button"
        styleType="outlined"
        onClick={updateDownloadModalVisibility}
      >
        <div className={classes.buttonInnerContainer}>
          <FontAwesomeIcon icon={faDownload} className={classes.actionIcon} />
          Download history
        </div>
      </ButtonV2>
      <div className={classes.uploadBtn}>
        <ButtonV2
          smallVariant
          data-testid="upload-metering-button"
          styleType="outlined"
          onClick={updateUploadModalVisibility}
        >
          <div className={classes.buttonInnerContainer}>
            <FontAwesomeIcon icon={faUpload} className={classes.actionIcon} />
            Upload metering record
          </div>
        </ButtonV2>
      </div>
      <DownloadMeteringHistoryModal
        open={openDownloadModal}
        onClose={updateDownloadModalVisibility}
      />

      <CreateMeteringFormatSelector
        showModal={openUploadModal}
        updateModalVisibility={updateUploadModalVisibility}
      />
    </div>
  )
}

export const ContractListingV2 = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [meteringData, setMeteringData] = useState<MeteringData>({})
  const [changedContracts, setChangedContracts] = useState<{
    [contractId: string]: ChangedContractInfo
  }>({})
  const [validationErrors, setValidationErrors] = useState<{
    [contractId: string]: { [entitlementId: string]: string }
  }>({})
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const navigate = useNavigate()
  const { contractsData } = useSelector<ContractsListingState>(
    state => state.contractsListing
  )

  useSyncCloudMarketplace()

  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const cloudProvider: CloudMarketplace = routeCloud
    ? (routeCloud.toUpperCase() as CloudMarketplace)
    : selectedCloudTypeProductListing

  const contracts: Contract[] = contractsData.contracts.rows

  const goToSubscriptionsDetail = (subscriptionId: string) => {
    const path =
      routeCloud && accountId
        ? `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionId}/detail`
        : `/flyout/subscriptions/${subscriptionId}/detail`
    navigate(path)
  }

  const [openDownloadModal, setDownloadModal] = useState<boolean>(false)
  const updateDownloadModalVisibility = () => {
    setDownloadModal(!openDownloadModal)
  }

  const [openUploadModal, setUploadModal] = useState<boolean>(false)
  const updateUploadModalVisibility = () => {
    setUploadModal(!openUploadModal)
  }

  const { changedCount, changedTotal } = useMemo(() => {
    let count = 0
    let total: number | null = 0
    let hasNullTotals = false

    Object.entries(meteringData).forEach(([contractId, entitlements]) => {
      const hasChanges = Object.keys(entitlements).length > 0

      if (hasChanges) {
        count++

        // Check if any entitlement in this contract has null total
        const contractHasNulls = Object.values(entitlements).some(
          item => item.total === null
        )

        if (contractHasNulls) {
          hasNullTotals = true
        } else {
          const contractSum = Object.values(entitlements).reduce(
            (sum, item) => sum + (item.total || 0),
            0
          )
          total! += contractSum
        }
      }
    })

    return {
      changedCount: count,
      changedTotal: hasNullTotals ? null : total,
    }
  }, [meteringData])

  const handleChange = useCallback(
    async (
      contractId: string,
      entitlementId: string,
      values: { meteringQuantityConsumed: string },
      price?: number | null
    ) => {
      const quantity = values.meteringQuantityConsumed
      const keyName = getKeyNameForUpdateMetering(cloudProvider)!

      dispatch(
        updateEntitlementsOrDimensions(contractId, {
          meteringId: entitlementId,
          values: quantity,
          keyName,
        })
      )

      // Handle empty quantity case
      if (quantity === '') {
        setMeteringData(prev => {
          const updated = { ...prev }
          if (updated[contractId]) {
            delete updated[contractId][entitlementId]
            if (Object.keys(updated[contractId]).length === 0) {
              delete updated[contractId]
            }
          }
          return updated
        })

        // Update changed contracts
        setChangedContracts(prev => {
          const updated = { ...prev }
          delete updated[contractId]
          return updated
        })

        setValidationErrors(prev => {
          const updated = { ...prev }
          if (updated[contractId]) {
            delete updated[contractId][entitlementId]
            if (Object.keys(updated[contractId]).length === 0) {
              delete updated[contractId]
            }
          }
          return updated
        })
        return
      }

      const parsedQuantity = parseFloat(quantity)
      if (cloudProvider === 'AZURE') {
        if (isNaN(parsedQuantity)) {
          setValidationErrors(prev => {
            const updated = { ...prev }
            if (updated[contractId] && updated[contractId][entitlementId]) {
              delete updated[contractId][entitlementId]
              if (Object.keys(updated[contractId]).length === 0) {
                delete updated[contractId]
              }
            }
            return updated
          })
          return
        }

        if (parsedQuantity <= 0) {
          setValidationErrors(prev => ({
            ...prev,
            [contractId]: {
              ...prev[contractId],
              [entitlementId]: 'Quantity must be greater than 0',
            },
          }))
          return
        }

        setValidationErrors(prev => {
          const updated = { ...prev }
          if (updated[contractId] && updated[contractId][entitlementId]) {
            delete updated[contractId][entitlementId]
            if (Object.keys(updated[contractId]).length === 0) {
              delete updated[contractId]
            }
          }
          return updated
        })
      }
      if (price == null || isNaN(price)) {
        setMeteringData(prev => ({
          ...prev,
          [contractId]: {
            ...prev[contractId],
            [entitlementId]: {
              quantity,
              total: null,
            },
          },
        }))

        const contract = contracts.find(c => c.id === contractId)
        if (contract) {
          setChangedContracts(prev => ({
            ...prev,
            [contractId]: {
              buyerLogoUrl: contract.buyerLogoUrl,
              buyerIdentifier:
                contract.buyerCompanyName ||
                contract.cloudMarketplaceContractId,
              totalAmount: null,
            },
          }))
        }
        return
      }

      if (isNaN(parsedQuantity) && cloudProvider === 'AWS') {
        return
      }

      const total = parsedQuantity * price

      setMeteringData(prev => ({
        ...prev,
        [contractId]: {
          ...prev[contractId],
          [entitlementId]: {
            quantity,
            total,
          },
        },
      }))

      // Update changed contracts
      const contract = contracts.find(c => c.id === contractId)
      if (contract) {
        setChangedContracts(prev => ({
          ...prev,
          [contractId]: {
            buyerLogoUrl: contract.buyerLogoUrl,
            buyerIdentifier:
              contract.buyerCompanyName || contract.cloudMarketplaceContractId,
            totalAmount:
              Object.values(meteringData[contractId] || {}).reduce(
                (sum, item) => sum + (item.total || 0),
                0
              ) +
              total -
              (meteringData[contractId]?.[entitlementId]?.total || 0),
          },
        }))
      }
    },
    [contracts, dispatch, cloudProvider, meteringData]
  )

  const calculateContractTotal = (contractId: string): number | null => {
    if (!meteringData[contractId]) return null

    const entitlements = meteringData[contractId]
    const entitlementValues = Object.values(entitlements)

    if (entitlementValues.length === 0) return null

    const hasNullTotals = entitlementValues.some(item => item.total === null)

    if (hasNullTotals) return null

    return entitlementValues.reduce((sum, item) => sum + (item.total || 0), 0)
  }

  const hasContractChanges = useCallback(
    (contractId: string) => {
      return (
        meteringData[contractId] &&
        Object.keys(meteringData[contractId]).length > 0
      )
    },
    [meteringData]
  )

  const handleSubmit = useCallback(async () => {
    const sucess = (await dispatch(
      sendContractsDataV2(contracts, accountId, contractSendMessage)
    )) as unknown as boolean
    if (sucess) {
      handleClear()
      setTimeout(() => {
        const usageMeteringPath = accountId
          ? `/flyout/${cloudProvider}/${accountId}/usage-metering`
          : '/flyout/usage-metering'
        navigate(usageMeteringPath)
      }, 1000)
    }
    handleCloseModal()
  }, [contracts])

  const handleCloseModal = useCallback(() => {
    setOpenModal(false)
  }, [])

  const handleOpenModal = useCallback(() => {
    if (cloudProvider === 'AZURE') {
      const errors: {
        [contractId: string]: { [entitlementId: string]: string }
      } = {}

      Object.entries(meteringData).forEach(([contractId, entitlements]) => {
        Object.entries(entitlements).forEach(([entitlementId, data]) => {
          const parsedQuantity = parseFloat(data.quantity)
          if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
            if (!errors[contractId]) {
              errors[contractId] = {}
            }
            errors[contractId][entitlementId] =
              'Quantity must be greater than 0'
          }
        })
      })

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors)
        return
      }
    }

    setOpenModal(true)
  }, [meteringData])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
    }
  }

  const handleClear = useCallback(async () => {
    const inputElements = document.querySelectorAll<HTMLInputElement>(
      'input[type="number"]'
    )
    inputElements.forEach(input => {
      input.value = ''
    })

    setMeteringData({})
    setChangedContracts({})
    setValidationErrors({})

    const keyName = getKeyNameForUpdateMetering(cloudProvider)!
    const contractData = contracts.map(contract => {
      if (keyName === 'entitlements' && contract.entitlements) {
        return {
          ...contract,
          entitlements: {
            ...contract.entitlements,
            rows: contract.entitlements.rows.map(row => ({
              ...row,
              meteringQuantityConsumed: NaN,
            })),
          },
        }
      } else if (keyName === 'dimensions' && contract.dimensions) {
        return {
          ...contract,
          dimensions: {
            ...contract.dimensions,
            rows: contract.dimensions.rows.map(row => ({
              ...row,
              meteringQuantityConsumed: NaN,
            })),
          },
        }
      } else if (keyName === 'plan' && contract.plan) {
        return {
          ...contract,
          plan: {
            ...contract.plan,
            dimensions: contract.plan.dimensions.map(dimension => ({
              ...dimension,
              meteringQuantityConsumed: NaN,
            })),
          },
        }
      } else {
        return contract
      }
    })

    const updatedData = {
      ...contractsData,
      contracts: {
        rows: [...contractData],
      },
    }
    await dispatch(setContracts(updatedData))
  }, [cloudProvider, contracts, dispatch, contractsData])

  const customHeader = ({
    unit,
    cloudProvider,
  }: {
    unit: string
    cloudProvider: CloudMarketplace
  }) => {
    const updatedUnit = unit ? transformMetric(unit) : '-'
    return {
      dimensionLabel: (
        <TableCell
          className={clsx(classes.tableCell, classes.dimensionTableCell)}
        >
          <div className={classes.customTableHeading}>Dimensions</div>
        </TableCell>
      ),
      unit: (
        <TableCell className={clsx(classes.tableCell, classes.priceTableCell)}>
          <div className={classes.tableHeading}>
            Price {cloudProvider === 'AWS' ? 'per' : ''} {updatedUnit}
          </div>
        </TableCell>
      ),
      qty: (
        <TableCell
          className={clsx(classes.tableCell, classes.quantityTableCell)}
        >
          <div className={classes.tableHeading}>Quantity</div>
        </TableCell>
      ),
      total: (
        <TableCell
          className={classes.totalTableCell}
          style={{ paddingRight: '12px' }}
        >
          <div className={classes.tableHeading}>Total</div>
        </TableCell>
      ),
    }
  }

  const getContractMeteringRowsBasedOnCloud = ({
    contract,
    productType,
  }: {
    contract: Contract
    productType?: PlanType
  }): Record<string, any> => {
    switch (cloudProvider) {
      case 'AWS': {
        const filteredEntitlements = contract.entitlements?.rows?.filter(
          row =>
            (productType === PlanType.SAAS_CONTRACT &&
              row.dimensionIsAdditionalUsageAllowed) ||
            productType === PlanType.SAAS_SUBSCRIPTION
        )
        const filteredLength = filteredEntitlements?.length

        const customHeaderObject = customHeader({
          unit: filteredEntitlements?.[0]?.unit ?? '',
          cloudProvider: cloudProvider,
        })
        const customObject = filteredEntitlements?.map(
          (entitlement: Entitlement, index: number) => {
            return {
              id: entitlement.entitlementId,
              dimensionLabel: (
                <div
                  className={clsx(classes.tableValues, classes.dimensionGap)}
                >
                  <ResponsiveColumn linkText={entitlement.dimensionLabel} />
                </div>
              ),
              unit: (
                <div className={classes.priceContainer}>
                  {entitlement.price ? (
                    <>
                      <div className={classes.dollar}>$</div>
                      <div className={classes.total}>
                        {formatToDecimalPlaces(entitlement.price, 3)}
                      </div>
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              ),
              qty: (
                <div
                  className={clsx(classes.tableValues, classes.qtyGap)}
                  onKeyDown={handleKeyDown}
                  data-testid={`metering-input-${contract.id}-${entitlement.entitlementId}`}
                >
                  <TextInputV2
                    keyName={'meteringQuantityConsumed'}
                    value={''}
                    isReadOnly={contract.listingStatus === 'EXPIRED'}
                    type="number"
                    onChange={event => {
                      const { value } = event.target
                      handleChange(
                        contract.id,
                        entitlement.entitlementId,
                        { meteringQuantityConsumed: value },
                        entitlement.price
                      )
                    }}
                    showSpinner={false}
                    minNumber={0}
                    keyToPrevent={numberKeyToPrevent}
                    size="small"
                    title={''}
                    placeHolder={''}
                    isError={
                      !!validationErrors[contract.id]?.[
                        entitlement.entitlementId
                      ]
                    }
                    errorText={
                      validationErrors[contract.id]?.[
                        entitlement.entitlementId
                      ] || ''
                    }
                    isRequired={false}
                    alignRight
                  />
                </div>
              ),
              total: (
                <div
                  className={clsx(
                    classes.customTableCell,
                    classes.tableValues,
                    classes.totalGap
                  )}
                  data-testid="total-amount"
                >
                  <div className={classes.priceContainer}>
                    {meteringData[contract.id]?.[entitlement.entitlementId] !==
                    undefined ? (
                      typeof meteringData[contract.id][
                        entitlement.entitlementId
                      ].total === 'number' ? (
                        <div className={classes.totalWrapper}>
                          <div className={classes.dollar}>$</div>
                          <div className={classes.total}>
                            {formatToDecimalPlaces(
                              meteringData[contract.id][
                                entitlement.entitlementId
                              ].total as number,
                              3
                            )}
                          </div>
                        </div>
                      ) : (
                        '-'
                      )
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              ),
            }
          }
        )
        return {
          customHeaderObject,
          customObject: customObject || [],
          filteredLength: filteredLength,
          rows: contract.entitlements?.rows,
        }
      }
      case 'AZURE': {
        const customHeaderObject = customHeader({
          unit: contract.dimensions?.rows?.[0]?.unitOfMeasure ?? '',
          cloudProvider: cloudProvider,
        })
        const customObject = contract.dimensions?.rows?.map(
          (dimension: ContractListingDimensionFields) => {
            return {
              id: dimension.dimensionId,
              dimensionLabel: (
                <div
                  className={clsx(classes.tableValues, classes.dimensionGap)}
                >
                  <ResponsiveColumn linkText={dimension?.dimensionLabel} />
                </div>
              ),
              unit: (
                <div className={classes.priceContainer}>
                  {dimension?.pricePerUnit ? (
                    <>
                      <div className={classes.dollar}>$</div>
                      <div className={classes.total}>
                        {formatToDecimalPlaces(dimension?.pricePerUnit, 3)}
                      </div>
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              ),
              qty: (
                <div
                  className={clsx(classes.tableValues, classes.qtyGap)}
                  onKeyDown={handleKeyDown}
                  data-testid={`metering-input-${contract.id}-${dimension.dimensionId}`}
                >
                  <TextInputV2
                    keyName={'meteringQuantityConsumed'}
                    value={''}
                    isReadOnly={contract.listingStatus === 'EXPIRED'}
                    type="number"
                    onChange={event => {
                      const { value } = event.target
                      handleChange(
                        contract.id,
                        dimension.dimensionId,
                        { meteringQuantityConsumed: value },
                        dimension?.pricePerUnit
                      )
                    }}
                    showSpinner={false}
                    minNumber={0}
                    keyToPrevent={numberKeyToPrevent}
                    size="small"
                    title={''}
                    placeHolder={''}
                    isError={
                      !!validationErrors[contract.id]?.[dimension.dimensionId]
                    }
                    errorText={
                      validationErrors[contract.id]?.[dimension.dimensionId] ||
                      ''
                    }
                    isRequired={false}
                    alignRight
                  />
                </div>
              ),
              total: (
                <div
                  className={clsx(
                    classes.customTableCell,
                    classes.tableValues,
                    classes.totalGap
                  )}
                  data-testid="total-amount"
                >
                  <div className={classes.priceContainer}>
                    {meteringData[contract.id]?.[dimension.dimensionId] !==
                    undefined ? (
                      typeof meteringData[contract.id][dimension.dimensionId]
                        .total === 'number' ? (
                        <div className={classes.totalWrapper}>
                          <div className={classes.dollar}>$</div>
                          <div className={classes.total}>
                            {formatToDecimalPlaces(
                              meteringData[contract.id][dimension.dimensionId]
                                .total as number,
                              3
                            )}
                          </div>
                        </div>
                      ) : (
                        '-'
                      )
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              ),
            }
          }
        )
        return {
          customHeaderObject,
          customObject: customObject,
          filteredLength: contract.dimensions?.rows?.length,
          rows: contract.dimensions?.rows,
        }
      }
      case 'GCP':
        return { customObject: [], filteredLength: 0, rows: [] }
      default:
        return { customObject: [], filteredLength: 0, rows: [] }
    }
  }

  return (
    <div data-testid="contract-listing-v2-container">
      {routeCloud !== 'azure' && (
        <div
          className={classes.topContainer}
          data-testid="contract-listing-v2-top-container"
        >
          <div></div>
          <div className={classes.btnContainer}>
            <ActionButtons
              updateDownloadModalVisibility={updateDownloadModalVisibility}
              openDownloadModal={openDownloadModal}
              updateUploadModalVisibility={updateUploadModalVisibility}
              openUploadModal={openUploadModal}
            />
          </div>
        </div>
      )}
      <div
        className={classes.bottomContainer}
        data-testid="contract-listing-v2-bottom-container"
      >
        <div className={classes.root}>
          <div className={classes.contractsContainer}>
            {contracts.map(contract => {
              const productType = contract.productType

              const { customHeaderObject, customObject, filteredLength, rows } =
                getContractMeteringRowsBasedOnCloud({ contract, productType })

              return (
                <div key={contract.id}>
                  <CardsContainer
                    addBorder={hasContractChanges(contract.id)}
                    hoverEffect
                    handleClick={() => goToSubscriptionsDetail(contract.id)}
                  >
                    <OfferCard
                      customLogo={
                        <BuyerLogo
                          logoUrl={contract.buyerLogoUrl}
                          classes={classes}
                        />
                      }
                      name={
                        <div className={classes.nameContainer}>
                          <div
                            className={classes.name}
                            onClick={e => e.stopPropagation()}
                          >
                            {contract.buyerCompanyName ? (
                              contract.buyerCompanyName
                            ) : (
                              <>
                                <CustomClipboard
                                  children={contract.cloudMarketplaceContractId}
                                  data-testid="custom-clipboard"
                                />
                              </>
                            )}
                          </div>
                          <LabraToolTipV2
                            tooltipPlacement={'top'}
                            description={'View contract details'}
                            tooltipPlacmentGap={'0px'}
                          >
                            <FontAwesomeIcon
                              className={classes.externalIcon}
                              icon={faExternalLinkAlt}
                              onClick={e => {
                                e.stopPropagation()
                                goToSubscriptionsDetail(contract.id)
                              }}
                              data-testid="external-icon"
                            />
                          </LabraToolTipV2>
                        </div>
                      }
                      rightSideSection={
                        <div className={classes.rightSideContainer}>
                          <div className={classes.emptyHeadingContainer}>
                            <div className={classes.emptyHeading}>
                              Last metering:{' '}
                              {isEmpty(contract.lastMeteringInfoSentAt)
                                ? '--'
                                : DateTimeLuxon.fromISO(
                                    contract.lastMeteringInfoSentAt!
                                  ).toFormat(standardLabraDateFormat)}
                            </div>
                          </div>
                          {/* TO BE TAKEN LATER */}
                          {/* {!isEmpty(contract.lastMeteringInfoSentAt) && (
                              <div
                                className={
                                  status
                                    ? classes.successCircle
                                    : classes.failedCircle
                                }
                              >
                                <FontAwesomeIcon
                                  icon={status ? faCheck : faExclamation}
                                  width={14}
                                  height={14}
                                  className={classes.icon}
                                />
                              </div>
                            )} */}
                          {/* <div className={classes.verticalDivider}></div>
                          <div
                            className={
                              !isEmpty(contract.lastMeteringInfoSentAt)
                                ? ''
                                : classes.downloadIcon
                            }
                          >
                            <LabraToolTipV2
                              tooltipPlacement={"top-end"}
                              description={'Download metering history'}
                              tooltipPlacmentGap={'0px'}
                            >
                              <FontAwesomeIcon
                                icon={faDownload}
                                width={18}
                                height={18}
                                color="#1942C5"
                              />
                            </LabraToolTipV2>
                          </div> */}
                        </div>
                      }
                      isLastCard
                      customiseForBuyer
                    />
                    <div>
                      {contract.planName && (
                        <div className={classes.planTitle}>
                          <div>Purchased Plan - </div>
                          <div className={classes.planTitleText}>
                            {contract.planName || ''}
                          </div>
                        </div>
                      )}
                      <div className={classes.tableWrapper}>
                        <TableComponentV2
                          keyName={'contracts-dimensional-table'}
                          mapping={mapping}
                          customHeader={customHeaderObject || []}
                          objects={customObject || []}
                          count={rows?.length || 0}
                          showCheckbox={false}
                          hasMoreData={false}
                          fetchMoreData={() => {}}
                          selectedItem={[]}
                          onChangeCheckbox={() => {}}
                          isLoading={false}
                          isBasicTable={true}
                          removeGap={true}
                          tableHeight={'250px'}
                          removeBasicTablePadding={
                            rows ? filteredLength! > 4 : false
                          }
                        />
                      </div>
                      <div
                        className={clsx(
                          classes.totalContainer,
                          filteredLength! > 4 ? classes.extraGap : ''
                        )}
                      >
                        <div className={classes.totalAmount}>Total amount</div>
                        <div
                          className={clsx(
                            classes.amountContainer,
                            classes.noAmount
                          )}
                        >
                          {calculateContractTotal(contract.id) === null ? (
                            <div className={classes.emptyHeading}>-</div>
                          ) : (
                            <>
                              <div className={classes.dollar}>$</div>
                              <div
                                className={classes.amount}
                                data-testid="final-total-amount"
                              >
                                {formatToDecimalPlaces(
                                  calculateContractTotal(contract.id)!,
                                  3
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardsContainer>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div data-testid="contracts-v2-footer-wrapper">
        <ContractsV2FooterSubmit
          onSubmit={handleOpenModal}
          onClear={handleClear}
          submitBtnTitle="Submit metering record"
          count={changedCount}
          totalAmount={changedTotal}
          key="footer-submit"
        />
        <ContractListingV2Modal
          openModal={openModal}
          handleSubmit={handleSubmit}
          handleCloseModal={handleCloseModal}
          changedContracts={changedContracts}
          count={changedCount}
          totalAmount={changedTotal}
        />
      </div>
      <SnackBar />
    </div>
  )
}
